import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCausas } from '@/hooks/useCausas';
import { cn } from '@/lib/utils';
import type { PreviewObra } from '@adelante/shared';
import { CircleNotch, XCircle } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';

interface FilaNC {
  key: string;
  obra: string;
  codigo: string;
  nombre: string;
  es_critica: boolean;
  pct: number;
  nc_existente: string | null;
}

interface Props {
  abierto: boolean;
  onClose: () => void;
  /** Todas las obras del preview (el cierre procesa TODAS, no solo las filtradas). */
  obras: PreviewObra[];
  /** Mapa NC actual del padre: "obra|sub" → código de causa. */
  ncMap: Record<string, string>;
  onGuardar: (nc: Record<string, string>) => void;
}

/**
 * Modal para asignar NC (no cumplimiento) a las subpartidas pendientes del
 * sprint actual antes del Corte A. Soporta edición múltiple: seleccionás varias
 * filas y les asignás una causa de un solo golpe.
 */
export function AsignarNCCierreDialog({ abierto, onClose, obras, ncMap, onGuardar }: Props) {
  const causasQ = useCausas();
  const causas = (causasQ.data?.data ?? []).filter((c) => c.aplica_nc && c.activo);

  // Filas = subpartidas del sprint actual NO completadas, SOLO de obras que no
  // avanzan (críticas incompletas). Las que avanzan/finalizan o no tienen
  // críticas no requieren NC.
  const filas = useMemo<FilaNC[]>(() => {
    const out: FilaNC[] = [];
    for (const o of obras) {
      // Congeladas (en_espera) NO se procesan en el cierre (el backend solo cierra
      // en_ejecucion), así que no requieren NC → se excluyen de la lista.
      if (o.estado === 'en_espera') continue;
      if (o.va_a_avanzar || o.avanzo_semana || o.criticas_total === 0) continue;
      for (const s of o.subs) {
        if (s.completada) continue;
        out.push({
          key: `${o.codigo}|${s.sub_partida_id}`,
          obra: o.codigo,
          codigo: s.codigo,
          nombre: s.nombre,
          es_critica: s.es_critica,
          pct: s.pct_completado,
          nc_existente: s.nc_causa,
        });
      }
    }
    return out;
  }, [obras]);

  const [local, setLocal] = useState<Record<string, string>>({});
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<string>('');

  // Al abrir: sembrar desde el mapa del padre, limpiar selección.
  useEffect(() => {
    if (abierto) {
      setLocal({ ...ncMap });
      setSel(new Set());
      setBulk('');
    }
  }, [abierto, ncMap]);

  const causaDe = (f: FilaNC) => local[f.key] ?? f.nc_existente ?? '';
  const sinNC = filas.filter((f) => !causaDe(f)).length;

  function setCausa(key: string, codigo: string) {
    setLocal((prev) => ({ ...prev, [key]: codigo }));
  }
  function toggle(key: string) {
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }
  const todasSel = filas.length > 0 && filas.every((f) => sel.has(f.key));
  function toggleTodas() {
    setSel(todasSel ? new Set() : new Set(filas.map((f) => f.key)));
  }
  function aplicarBulk() {
    if (!bulk || sel.size === 0) return;
    setLocal((prev) => {
      const n = { ...prev };
      for (const k of sel) n[k] = bulk;
      return n;
    });
    setSel(new Set());
    setBulk('');
  }
  function handleGuardar() {
    onGuardar(local);
    onClose();
  }

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[760px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" weight="fill" aria-hidden="true" />
            Asignar no cumplimientos (NC)
          </DialogTitle>
          <DialogDescription>
            Toda subpartida no completada del sprint actual necesita una causa antes del cierre.
            {sinNC > 0 ? (
              <>
                {' '}
                Quedan <strong className="text-red-600">{sinNC}</strong> sin causa.
              </>
            ) : (
              <> Todas tienen causa ✓</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Barra de edición múltiple */}
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-zinc-50 px-3 py-2">
          <Checkbox
            checked={todasSel}
            onCheckedChange={toggleTodas}
            aria-label="Seleccionar todas"
          />
          <span className="text-xs text-muted-foreground">
            {sel.size > 0
              ? `${sel.size} seleccionada${sel.size === 1 ? '' : 's'}`
              : 'Seleccioná para asignar en bloque'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Select value={bulk || undefined} onValueChange={setBulk}>
              <SelectTrigger className="h-8 w-[220px]">
                <SelectValue placeholder="Causa…" />
              </SelectTrigger>
              <SelectContent>
                {causas.map((c) => (
                  <SelectItem key={c.codigo} value={c.codigo}>
                    {c.codigo} — {c.descripcion}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={aplicarBulk} disabled={!bulk || sel.size === 0}>
              Asignar{sel.size > 0 ? ` a ${sel.size}` : ''}
            </Button>
          </div>
        </div>

        {/* Tabla de pendientes */}
        <div className="max-h-[50vh] overflow-y-auto rounded-md border border-border">
          {causasQ.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando causas…
            </div>
          ) : filas.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No hay subpartidas pendientes. Todo listo para cerrar.
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {filas.map((f) => {
                  const causa = causaDe(f);
                  return (
                    <tr
                      key={f.key}
                      className={cn(
                        'border-b border-border last:border-0',
                        !causa && 'bg-red-50/40',
                      )}
                    >
                      <td className="w-8 px-2 py-1.5 align-middle">
                        <Checkbox
                          checked={sel.has(f.key)}
                          onCheckedChange={() => toggle(f.key)}
                          aria-label={`Seleccionar ${f.obra} ${f.codigo}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 align-middle font-mono text-xs">{f.obra}</td>
                      <td className="px-2 py-1.5 align-middle">
                        <span className="font-mono text-xs text-muted-foreground">{f.codigo}</span>{' '}
                        <span>{f.nombre}</span>
                        {f.es_critica && (
                          <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700">
                            crítica
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right align-middle tabular-nums text-xs text-muted-foreground">
                        {Math.round(f.pct)}%
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        <Select
                          value={causa || undefined}
                          onValueChange={(v) => setCausa(f.key, v)}
                        >
                          <SelectTrigger
                            className={cn('h-8 w-[210px]', !causa && 'border-red-300 text-red-600')}
                          >
                            <SelectValue placeholder="Sin causa" />
                          </SelectTrigger>
                          <SelectContent>
                            {causas.map((c) => (
                              <SelectItem key={c.codigo} value={c.codigo}>
                                {c.codigo} — {c.descripcion}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar}>
            Guardar{sinNC === 0 ? ' — todo cubierto ✓' : ` (${sinNC} sin causa)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
